import React from "react";
import { Card, CircleIconContainer, CurrencyPrice } from "@letsbit/milkylb";
import dayjs from "dayjs";
import classNames from "classnames";
import { useIntl } from "react-intl";
import {
  ADJUSTMENT_SUBCATEGORY_TYPE,
  HISTORY_OPERATION_STATE_TYPE,
  HISTORY_OPERATION_TYPE,
  RECORD_STATE,
} from "@letsbit/malcolmlb";

import { useFormatter } from "hooks/utils/useFormatter";
import { formatLastFour } from "utils/paymentCardUtils";

import { CurrencyOperationCardProps } from "./types";
import { messages, historyMessages } from "./messages";
import { useTranslationConstants } from "hooks/utils/useTranslationConstants";

import "./styles.scss";

export const CurrencyOperationCard: React.FC<CurrencyOperationCardProps> = ({
  className,
  operation,
  onClick,
}) => {
  const intl = useIntl();
  const { formatValue } = useFormatter();
  const {
    getAdjustmentsOperations,
    getCardTransactionOperations,
    getMerkOperations,
    getMerkOrderTitle,
  } = useTranslationConstants();

  const getStatus = (state?: HISTORY_OPERATION_STATE_TYPE): RECORD_STATE => {
    switch (state) {
      case "succeed":
      case "accepted":
      case "authorized":
      case "completed":
        return RECORD_STATE.SUCCEED;
      case "waiting_authorization":
        return RECORD_STATE.WAITING_AUTHORIZATION;
      case "rejected":
      case "failed":
      case "canceled":
      case "sync_failed":
      case "unauthorized":
      case "revoked":
        return RECORD_STATE.FAILED;
      default:
        return RECORD_STATE.PENDING;
    }
  };

  const getStatusText = (state?: RECORD_STATE) => {
    let st;
    let stClass;

    switch (state) {
      case RECORD_STATE.PENDING:
        stClass = "currency-operation-card__status--in-progress";
        st = intl.formatMessage(messages.statusInProgress);
        break;
      case RECORD_STATE.SUCCEED:
        stClass = "currency-operation-card__status--ok";
        st = intl.formatMessage(messages.statusOk);
        break;
      case RECORD_STATE.UNAUTHORIZED:
      case RECORD_STATE.FAILED:
        stClass = "currency-operation-card__status--failed";
        st = intl.formatMessage(messages.statusFailed);
        break;
      case RECORD_STATE.WAITING_AUTHORIZATION:
        stClass = "currency-operation-card__status--in-progress";
        st = intl.formatMessage(messages.statusWaitingAuthorization);
        break;

      default:
        break;
    }
    return { st, stClass };
  };

  const getOperation = () => {
    switch (operation.type) {
      case HISTORY_OPERATION_TYPE.DEPOSIT: {
        const opState = getStatus(operation.extra_data.state);
        return {
          title:
            operation.extra_data.user_full_name ??
            intl.formatMessage(historyMessages[operation.type]),
          amount: `+ ${formatValue(operation.amount, operation.precision)}`,
          state: getStatus(operation.extra_data.state),
          extra_data: getStatusText(opState).st,
          extra_data_classname: getStatusText(opState).stClass,
        };
      }
      case HISTORY_OPERATION_TYPE.TRADE:
      case HISTORY_OPERATION_TYPE.AUTOSWAP: {
        const opState = getStatus(operation.extra_data.state);
        return {
          title: intl.formatMessage(historyMessages[operation.type]),
          amount: `+ ${formatValue(operation.amount, operation.precision)}`,
          state: opState,
          extra_data:
            operation.extra_data.spent &&
            operation.extra_data.out_currency_ticker_id
              ? `- ${formatValue(
                  operation.extra_data.spent,
                  operation.extra_data.out_currency_precision,
                )} ${operation.extra_data.out_currency_ticker_id.toUpperCase()}`
              : "",
        };
      }
      case HISTORY_OPERATION_TYPE.WITHDRAW: {
        const opState = getStatus(operation.extra_data.state);
        return {
          title:
            operation.extra_data.user_full_name ??
            intl.formatMessage(historyMessages[operation.type]),
          amount: `- ${formatValue(operation.amount, operation.precision)}`,
          state: opState,
          extra_data: getStatusText(opState).st,
          extra_data_classname: getStatusText(opState).stClass,
        };
      }
      case HISTORY_OPERATION_TYPE.REFERRER_PAYOUT: {
        const opState = getStatus(operation.extra_data.state);
        return {
          title: intl.formatMessage(historyMessages[operation.type]),
          amount: `+ ${formatValue(operation.amount, operation.precision)}`,
          state: opState,
          extra_data: operation.extra_data.referee_email,
        };
      }
      case HISTORY_OPERATION_TYPE.INTEREST_PAYMENT: {
        const opState = getStatus(operation.extra_data.state);
        return {
          title: intl.formatMessage(historyMessages[operation.type]),
          amount: `+ ${formatValue(operation.amount, operation.precision)}`,
          state: opState,
          extra_data: operation.extra_data.rate
            ? ` ${formatValue(
                (+operation.extra_data.rate * 100).toString(),
              )} % TEA`
            : "",
        };
      }
      case HISTORY_OPERATION_TYPE.CARD_TRANSACTIONS: {
        const opState = getStatus(operation.extra_data.state);
        return {
          title: getCardTransactionOperations(
            operation.extra_data.type_card_transaction,
            operation.extra_data.state,
            operation.extra_data.merchant_name,
          ),
          amount: `${formatValue(operation.amount, operation.precision)}`,
          state: opState,
          extra_data: formatLastFour(operation.extra_data.last_four || ""),
        };
      }

      case HISTORY_OPERATION_TYPE.ADJUSTMENT: {
        const opState = getStatus(operation.extra_data.state);
        return {
          title: getAdjustmentsOperations(operation.extra_data.type_adjustment),
          amount: `${
            operation.extra_data.kind === ADJUSTMENT_SUBCATEGORY_TYPE.CREDIT
              ? "+"
              : "-"
          } ${formatValue(operation.amount, operation.precision)}`,
          state: opState,
          extra_data: getStatusText(opState).st,
          extra_data_classname: getStatusText(opState).stClass,
        };
      }

      case HISTORY_OPERATION_TYPE.REWARD: {
        const opState = getStatus(operation.extra_data.state);
        return {
          title: operation.extra_data.description ?? "",
          amount: `+ ${formatValue(operation.amount, operation.precision)}`,
          state: opState,
          extra_data: getStatusText(opState).st,
          extra_data_classname: getStatusText(opState).stClass,
        };
      }

      case HISTORY_OPERATION_TYPE.PAYMENT: {
        const opState = getStatus(operation.extra_data.state);
        const amount = `${formatValue(operation.amount, operation.precision)}`;
        return {
          title:
            operation.extra_data.friendly_name ||
            operation.extra_data.merchant_name ||
            "",
          amount: amount,
          state: opState,
          extra_data: getStatusText(opState).st,
          extra_data_classname: getStatusText(opState).stClass,
        };
      }
      case HISTORY_OPERATION_TYPE.WITHHOLDING: {
        const amount = `${formatValue(operation.amount, operation.precision)}`;
        return {
          title: intl.formatMessage(historyMessages[operation.type]),
          amount: amount,
          state: RECORD_STATE.SUCCEED,
          extra_data: getStatusText(RECORD_STATE.SUCCEED).st,
          extra_data_classname: getStatusText(RECORD_STATE.SUCCEED).stClass,
        };
      }
      case HISTORY_OPERATION_TYPE.WITHHOLDINGS_WITHHOLDING: {
        const amount = `${formatValue(operation.amount, operation.precision)}`;
        return {
          title: intl.formatMessage(historyMessages[operation.type]),
          amount: amount,
          state: RECORD_STATE.SUCCEED,
          extra_data: getStatusText(RECORD_STATE.SUCCEED).st,
          extra_data_classname: getStatusText(RECORD_STATE.SUCCEED).stClass,
        };
      }
      case HISTORY_OPERATION_TYPE.MERK_ACCOUNT_MOVEMENT: {
        return {
          title: getMerkOperations(
            operation.extra_data.type,
            operation.extra_data.provider,
          ),
          amount: `${
            operation.extra_data.type === "credit" ? "+" : "-"
          } ${formatValue(operation.amount, operation.precision)}`,
          state: RECORD_STATE.SUCCEED,
          extra_data: getStatusText(RECORD_STATE.SUCCEED).st,
          extra_data_classname: getStatusText(RECORD_STATE.SUCCEED).stClass,
        };
      }
      case HISTORY_OPERATION_TYPE.MERK_ORDER: {
        return {
          title: getMerkOrderTitle(
            operation.extra_data.asset?.id || "",
            operation.extra_data.side,
          ),
          amount: `${
            operation.extra_data.side === "buy" ? "-" : "+"
          } ${formatValue(operation.amount, operation.precision)}`,

          state: RECORD_STATE.SUCCEED,
          extra_data: getStatusText(RECORD_STATE.SUCCEED).st,
          extra_data_classname: getStatusText(RECORD_STATE.SUCCEED).stClass,
        };
      }
      default:
        return { title: "", amount: "", extra_data: "", subtitle: "" };
    }
  };

  return (
    <Card
      className={classNames(
        className,
        `currency-operation-card${
          getOperation().state === RECORD_STATE.FAILED ? "__rejected" : ""
        }`,
      )}
      icon={
        <CircleIconContainer
          classNameSvg="currency-operation-card__icon"
          icon={operation.icon_url}
        />
      }
      title={getOperation().title}
      subtitle={
        getOperation().subtitle || dayjs(operation.date).format("DD.MM.YYYY")
      }
      onClick={onClick}
    >
      <CurrencyPrice
        className={getOperation().extra_data_classname}
        price={getOperation().amount}
        priceCurrency={
          operation.type === HISTORY_OPERATION_TYPE.MERK_ORDER
            ? operation.extra_data.asset?.currency || ""
            : operation.type === HISTORY_OPERATION_TYPE.PAYMENT
              ? operation.ticker_id
                ? operation.ticker_id.toUpperCase()
                : "ARS"
              : operation.ticker_id
                ? operation.ticker_id.toUpperCase()
                : "-"
        }
        variation={
          getOperation().state === RECORD_STATE.FAILED
            ? intl.formatMessage(messages.statusFailed)
            : getOperation().extra_data || ""
        }
        variationState="neutral"
      />
    </Card>
  );
};

export default CurrencyOperationCard;
